import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("mysilo_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // iter-25: forward the visitor signature cookie as a header so the backend
  // can attach it to public chat sessions even on cross-origin preview URLs
  // where SameSite cookies would otherwise be stripped by axios defaults.
  try {
    const match = document.cookie.split("; ").find((c) => c.startsWith("mysilo_visitor_sig="));
    if (match) config.headers["X-Mysilo-Visitor-Sig"] = match.split("=")[1];
  } catch { /* ignore */ }
  // Jun 2026 (Randy) — forward the shared-bookmark id (from /s/<slug>?bookmark=)
  // so the backend grants read access to a conversation shared from a HIDDEN
  // silo across every public endpoint (silo info, dots/interactions, ask, etc.).
  try {
    const bm = new URLSearchParams(window.location.search).get("bookmark");
    if (bm) config.headers["X-Shared-Bookmark"] = bm;
  } catch { /* ignore */ }
  return config;
});

// Feb 17, 2026 — Normalize FastAPI 422 validation errors so components can
// safely render `err.response.data.detail` directly into a toast/JSX. By
// default FastAPI returns `detail` as an ARRAY of {type, loc, msg, input, url}
// objects, which crashes React when shoved into a string-expecting renderer
// ("Objects are not valid as a React child"). We collapse it into a single
// human-readable string here so every `toast.error(err.response.data.detail)`
// across the app becomes safe automatically.
//
// Also performs ONE silent retry for transient DB/network failures (503,
// "Network Error", or no `response`), so a brief MongoDB blip during a
// heavy refinery/workbench job doesn't immediately cascade into "Failed to
// load X" toasts everywhere. Idempotent GETs only — POST/PATCH/DELETE
// retries are unsafe.
// Jun 2026 — auth-probe paths where a 401 is a normal credential check
// (wrong password, etc.), NOT a dead session — don't bounce the user mid-login.
// Oct 2026 (pt-launch-target) — `/auth/session-from-cookie` and
// `/auth/exchange-handoff` join this list. They are session BOOTSTRAP probes:
// a 401/400 from either means "there was nothing to restore", never "your
// session died". Without the exemption, a PWA launch where the cookie restore
// lost the race to the handoff exchange tripped the expired-session path below
// — which wipes the token AND the cookie and redirects to /login, with a
// perfectly good session already in hand.
const AUTH_PROBE_PATHS = [
  "/auth/login", "/auth/2fa", "/auth/signup", "/auth/forgot", "/auth/reset",
  "/auth/session-from-cookie", "/auth/exchange-handoff",
];
let _sessionExpiredHandled = false;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Feb 22, 2026 (Randy pt89c-v) — diagnostic breadcrumb so we can trace
    // mystery 502s back to a specific endpoint. Logs one line per failed
    // request with method + URL + status. Cheap, safe, no PII exposure.
    try {
      const cfg = error?.config || {};
      const status = error?.response?.status;
      const url = (cfg.baseURL || "") + (cfg.url || "");
      const method = (cfg.method || "GET").toUpperCase();
      // Only noisy for real failures — skip normal 401 auth checks.
      if (status !== 401) {
        console.warn(`[api] ${method} ${url} → ${status || "(network)"}`, error?.response?.data);
      }
    } catch { /* logging must never crash */ }
    try {
      const cfg = error?.config || {};
      const isIdempotent = !cfg.method || ["get", "head"].includes(cfg.method.toLowerCase());
      const isNetwork = !error.response;
      const isTransient503 = error.response?.status === 503;
      const alreadyRetried = cfg.__retried;
      if (isIdempotent && (isNetwork || isTransient503) && !alreadyRetried) {
        cfg.__retried = true;
        await new Promise((r) => setTimeout(r, 800));
        return api.request(cfg);
      }
      // Jun 2026 — Expired/invalid session. If a token is stored but the
      // server rejects it (401), the session is dead. Rather than stranding
      // the user on a broken page behind a wall of "failed to load" toasts,
      // clear the stale token and send them to /login once.
      const reqUrl = cfg.url || "";
      const isAuthProbe = AUTH_PROBE_PATHS.some((p) => reqUrl.includes(p));
      let hadToken = false;
      try { hadToken = !!sessionStorage.getItem("mysilo_token"); } catch { /* storage off */ }
      if (error?.response?.status === 401 && hadToken && !isAuthProbe && !_sessionExpiredHandled) {
        _sessionExpiredHandled = true;
        try {
          sessionStorage.removeItem("mysilo_token");
          sessionStorage.removeItem("mysilo_user");
          document.cookie = "mysilo_session=; path=/; max-age=0; SameSite=Lax; Secure";
          sessionStorage.setItem("mysilo_session_expired", "1");
        } catch { /* storage off */ }
        try {
          if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
            window.location.assign("/login");
          }
        } catch { /* no window */ }
        return Promise.reject(error);
      }
      // Feb 20, 2026 — Surface SILO_VOUCHER_EMPTY (402) globally as a
      // window event so any page (silo public Q&A, silo wars launcher,
      // creator dash) can listen and show a top-up prompt without each
      // call site needing custom handling.
      const status402 = error?.response?.status === 402;
      const d402 = error?.response?.data?.detail;
      if (status402 && d402 && typeof d402 === "object" && d402.code === "SILO_VOUCHER_EMPTY") {
        try {
          window.dispatchEvent(new CustomEvent("mysilo:voucher-empty", { detail: d402 }));
        } catch { /* SSR / no window */ }
      }
      // Feb 28, 2026 — Platform-wide LLM guard. Backend raises 402 with
      // detail.code='LLM_GUARD' when a creator has no BYOK for the
      // resolved provider AND that provider's voucher bucket is empty.
      // The global LLMGuardModal listens for this event and pops the
      // teach-modal with top-up / BYOK CTAs.
      if (status402 && d402 && typeof d402 === "object" && d402.code === "LLM_GUARD") {
        try {
          window.dispatchEvent(new CustomEvent("llm-guard-blocked", { detail: d402 }));
        } catch { /* SSR / no window */ }
      }
      // Jun 2026 — preserve the structured 402 code in a stable field. The
      // normalization below collapses the detail object into a plain string,
      // so call sites (voice guard, staging dictation) read `guard_code`
      // instead of the (now-stringified) `detail.code`.
      if (status402 && d402 && typeof d402 === "object" && d402.code) {
        try { error.response.data.guard_code = d402.code; } catch { /* no-op */ }
      }
      // Normalize FastAPI 422 detail array → readable string.
      const d = error?.response?.data?.detail;
      if (Array.isArray(d)) {
        error.response.data.detail = d.map((x) => {
          if (!x) return "";
          if (typeof x === "string") return x;
          return x.msg || JSON.stringify(x);
        }).filter(Boolean).join("; ");
      } else if (d && typeof d === "object") {
        // Jun 2026 — keep the FULL structured detail object on a parallel field
        // before we stringify `detail` for safe React rendering. Call sites that
        // need fields like code / topup_cents / requires_auth (e.g. the visitor
        // meter wall, owner-BYOK nudge) read `detail_obj`.
        error.response.data.detail_obj = d;
        error.response.data.detail = d.msg || d.message || JSON.stringify(d);
      }
    } catch { /* never let normalization itself crash */ }
    return Promise.reject(error);
  },
);

export const saveAuth = (token, user) => {
  sessionStorage.setItem("mysilo_token", token);
  sessionStorage.setItem("mysilo_user", JSON.stringify(user));
  // May 24, 2026 — also mirror the JWT to a same-domain cookie so the
  // per-silo PWA manifest endpoint can read it server-side. iOS Safari
  // shares cookies (but partitions localStorage) with standalone PWA
  // launches, so the cookie path lets the manifest auto-include an
  // install-handoff token even before any React code runs. Cookie
  // expires after 30 days, scoped to the whole site, sent only on
  // same-site navigations (no cross-origin leak), and Secure (HTTPS).
  try {
    document.cookie = `mysilo_session=${encodeURIComponent(token)}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax; Secure`;
  } catch { /* ignore */ }
};
export const clearAuth = () => {
  sessionStorage.removeItem("mysilo_token");
  sessionStorage.removeItem("mysilo_user");
  try {
    document.cookie = "mysilo_session=; path=/; max-age=0; SameSite=Lax; Secure";
  } catch { /* ignore */ }
};
export const getToken = () => sessionStorage.getItem("mysilo_token");

// Jun 18, 2026 — Per-tab sessions. The auth token/user moved from localStorage
// (shared across every tab → "session bleed": a 2nd login in another tab wiped
// the first) to sessionStorage (scoped to a single tab). One-time migration so
// anyone already logged in stays logged in across this change: copy a legacy
// localStorage token into THIS tab's sessionStorage, then delete the localStorage
// copy so brand-new tabs correctly start signed-out (independent-tab behavior).
try {
  if (!sessionStorage.getItem("mysilo_token")) {
    const legacyToken = localStorage.getItem("mysilo_token");
    if (legacyToken) {
      sessionStorage.setItem("mysilo_token", legacyToken);
      const legacyUser = localStorage.getItem("mysilo_user");
      if (legacyUser) sessionStorage.setItem("mysilo_user", legacyUser);
    }
  }
  // Always purge the legacy shared copy so other/new tabs don't inherit it.
  localStorage.removeItem("mysilo_token");
  localStorage.removeItem("mysilo_user");
} catch { /* storage off */ }


// May 24, 2026 — backwards-compat: users who logged in before the
// mysilo_session cookie existed have a token in localStorage but no
// cookie. Mirror it on app boot so the PWA manifest cookie path works
// without forcing a re-login. Runs once on module load.
try {
  const existing = sessionStorage.getItem("mysilo_token");
  const hasCookie = typeof document !== "undefined"
    && document.cookie.split("; ").some((c) => c.startsWith("mysilo_session="));
  if (existing && !hasCookie) {
    document.cookie = `mysilo_session=${encodeURIComponent(existing)}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax; Secure`;
  }
} catch { /* ignore */ }
export const getUser = () => {
  try { return JSON.parse(sessionStorage.getItem("mysilo_user") || "null"); } catch { return null; }
};
export const setUser = (user) => {
  sessionStorage.setItem("mysilo_user", JSON.stringify(user));
};
